import React from 'react';
import { FormattedMessage } from 'react-intl';
import { sentryEnvironment } from 'js/sign-components/env';
import HfReactHelper from 'js/sign-components/common/hf-react-helper';
import { isProduction, isLocalEnvironment } from 'js/sign-components/common/ts-utils';

import { Title, Text } from '@dropbox/dig-components/typography';
import { TrafficRoadBlockedSpot } from '@dropbox/dig-illustrations/dist/cjs/spot/traffic-road-blocked';
import { Banner } from '@dropbox/dig-components/banner';
import { Button } from '@dropbox/dig-components/buttons';
import { UIIcon } from '@dropbox/dig-icons';
import { EmailLine, BugLine } from '@dropbox/dig-icons/assets';

import styles from './error-page.module.scss';

export type ErrorPageProps = {
  errorCode: string,
  errorTime: Date,
  error?: Error,
}

type ErrorBannerProps = {
  error: Error,
  errorCode: string,
}

function ErrorBanner({ error, errorCode }: ErrorBannerProps) {
  const [showStack, toggle] = React.useReducer((v) => !v, false);

  if (isProduction()) {
    return null;
  }

  return (
    <Banner
      className={styles.errorStackBanner}
      type="alert"
      variant='basic'
      withLeftIcon={false}
    >
      <Banner.Message>
        <Title size="small">{errorCode} &ndash; {error.name}: {error.message}</Title>
        {showStack && (
          <Text
            className={styles.errorStack}
            tagName="p"
            size='small'
            style={{ whiteSpace: 'pre' }}
            monospace
          >
            {error.stack}
          </Text>
        )}
        <Button variant='transparent' onClick={toggle}>
          Toggle stack
        </Button>
      </Banner.Message>
    </Banner>
  );
}

function ErrorPage({ errorCode, errorTime, error }: ErrorPageProps) {
  const [didEnableLocalSentryReporting, setDidEnableLocalSentryReporting] = React.useState(false);
  const [isEnablingLocalSentryReporting, setIsEnablingLocalSentryReporting] = React.useState(false);

  const errorDetails = isProduction()
    ? undefined
    : (error && <ErrorBanner errorCode={errorCode} error={error} />);

  const isSentryReportingEnabled = React.useMemo(() => {
    try {
      if (sentryEnvironment === 'development') {
        return window.localStorage.getItem('local-sentry-reporting-enabled') === 'y';
      }
    } catch (err) {
      // Do nothing.
    }
    return false;
  }, []);

  return (
    <div className={styles.container}>
      <Title size="large">
        <FormattedMessage
          id=""
          description="unknown error, message in error page"
          defaultMessage="An unexpected error has occurred." />
      </Title>
      <div className={styles.illustration}>
        <TrafficRoadBlockedSpot />
      </div>
      <div className={styles.errorHelpText}>
        <Text variant="paragraph">
          <FormattedMessage
            id=""
            description="error message"
            defaultMessage="Please try once more. If the error persists, please contact us with the error code and time below." />
        </Text>
      </div>
      <div className={styles.errorInfo}>
        <Text>
          <FormattedMessage
            id=""
            description="unknown error, message in error page"
            defaultMessage="Error code: {errorCode}"
            values={{
              errorCode: <Text tagName="span" isBold monospace>{errorCode}</Text>,
            }} />
        </Text>
        <br />
        <Text>
          <FormattedMessage
            id=""
            description="unknown error, message in error page, errorTime is simple time"
            defaultMessage="Error time: {errorTime}"
            values={{
              errorTime: <Text tagName="span" isBold monospace>{`${errorTime.toUTCString()}`}</Text>,
            }} />
        </Text>
        {!isProduction() && (
          <>
            <br />
            <Text>
              <FormattedMessage
                id=""
                description="unknown error, message in error page, errorTime is simple time"
                defaultMessage="Error environment: {errorTime}"
                values={{
                  errorTime: <Text tagName="span" isBold monospace>{sentryEnvironment}</Text>,
                }} />
            </Text>
          </>
        )}
      </div>
      <div className={styles.contactUsButton}>
        <Button
          withIconLeft={<UIIcon src={EmailLine} role="presentation" />}
          variant="opacity"
          href={HfReactHelper.getFaqUrl('requests/new')}
        >
          <FormattedMessage
            id=""
            description="button text in error page, when clicked reroutes user to conact page"
            defaultMessage="Contact us" />
        </Button>
      </div>
      {isLocalEnvironment() && !isSentryReportingEnabled && (
        <Banner
          variant="basic"
          type="warning"
          withLeftIcon={false}
          className={styles.sentryReportingBanner}
        >
          <Banner.Message>
            {didEnableLocalSentryReporting ? (
              <>
                <Text>
                  <FormattedMessage
                    id=""
                    description="Internal debug info text."
                    defaultMessage="Local Sentry reporting has been enabled." />
                </Text>
                <br />
                <Text>
                  <FormattedMessage
                    id=""
                    description="Internal debug info text."
                    defaultMessage="To disable local Sentry reporting in the future, click the {bugIcon} in the debug toolbar and click <em>Disable sentry reporting on dev machine</em>."
                    values={{
                      bugIcon: <UIIcon className={styles.bugIcon} src={BugLine} role="presentation" />,
                      em(...chunks: any[]) {
                        return <Text isBold>{chunks}</Text>;
                      },
                    }} />
                </Text>
              </>
            ) : (
              <>
                <Text isBold>
                  <FormattedMessage
                    id=""
                    description="Internal debug message; notice to QA engineers"
                    defaultMessage="Attention QA engineers!" />
                </Text>
                <br />
                <Text>
                  <FormattedMessage
                    id=""
                    description="Internal debug message; notice to QA engineers"
                    defaultMessage="This error was <em>not</em> sent to Sentry. If you are using your dev vm to QA and verify tickets, please enable local Sentry reporting."
                    values={{
                      em(...chunks: any[]) {
                        return <Text isBold>{chunks}</Text>;
                      },
                    }} />
                </Text>
                <br />
                <br />
                <Button
                  variant="transparent"
                  isLoading={isEnablingLocalSentryReporting}
                  onClick={() => {
                    setIsEnablingLocalSentryReporting(true);

                    // eslint-disable-next-line no-alert
                    if (confirm('Are you sure? You should ONLY enable local Sentry reporting if you are actively using your dev vm to QA and verify tickets. If you are unsure, please reach out to #ask-hs-frontend.')) {
                      window.localStorage.setItem('local-sentry-reporting-enabled', 'y');

                      // Update debug toolbar text if enabled.
                      if (document.getElementById('hs-debug-tools')) {
                        document.getElementById('hs-debug-tools__enable-sentry-reporting')!.textContent = 'Disable Sentry reporting on dev machine';
                      }

                      setTimeout(() => {
                        setDidEnableLocalSentryReporting(true);
                      }, 500);
                    } else {
                      setIsEnablingLocalSentryReporting(false);
                    }
                  }}
                >
                  <FormattedMessage
                    id=""
                    description="Text for a button which enables local Sentry reporting when pressed."
                    defaultMessage="Enable local Sentry reporting" />
                </Button>
              </>
            )}
          </Banner.Message>
        </Banner>
      )}
      {errorDetails}
    </div>
  );
}

export default ErrorPage;
